/**
 * The login screen - the only thing a signed-out visitor can reach.
 *
 * A centred card on the page plane, with the theme toggle available so the form respects
 * dark mode before there is any session. A wrong password comes back as a 401, which the
 * client turns into a thrown ApiError; that is caught here and shown as one flat message
 * ("check your email and password") rather than saying which half was wrong, matching the
 * server's deliberately vague response.
 */

import { Moon, ShieldCheck, Sun } from 'lucide-react'
import { useState } from 'react'

import { ApiError } from './api/client'
import { useLogin } from './hooks/useAuth'
import { useTheme } from './hooks/useTheme'

export function LoginPage() {
  const theme = useTheme()
  const login = useLogin()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    try {
      const status = await login.mutateAsync({ email, password })
      if (!status.authenticated) {
        setError('Incorrect email or password.')
      }
      // On success the auth-status query flips and App swaps in the dashboard.
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Incorrect email or password.')
      } else {
        setError(
          err instanceof Error ? `Could not sign in: ${err.message}` : 'Could not sign in.',
        )
      }
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <button
        type="button"
        onClick={theme.toggle}
        aria-label={`Switch to ${theme.resolved === 'dark' ? 'light' : 'dark'} theme`}
        className="absolute top-4 right-4 rounded-md border p-1.5 text-ink-secondary transition-colors hover:bg-surface-sunken"
      >
        {theme.resolved === 'dark' ? <Sun size={14} aria-hidden /> : <Moon size={14} aria-hidden />}
      </button>

      <div className="card w-full max-w-sm p-6">
        <div className="mb-5 flex items-center gap-2">
          <ShieldCheck size={18} aria-hidden className="text-accent" />
          <h1 className="text-lg font-semibold tracking-tight text-ink">WhiteHawk Health</h1>
        </div>
        <p className="mb-4 text-xs text-ink-secondary">Sign in to view the dashboard.</p>

        <form onSubmit={submit} className="space-y-3" noValidate>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-ink-muted">Email</span>
            <input
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-md border bg-surface px-2.5 py-2 text-sm text-ink placeholder:text-ink-muted"
              placeholder="ops@whitehawk.io"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-ink-muted">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-md border bg-surface px-2.5 py-2 text-sm text-ink"
            />
          </label>

          {error ? (
            <p role="alert" className="text-xs text-critical">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={login.isPending || !email || !password}
            className="w-full rounded-md border bg-accent-soft px-3 py-2 text-sm font-semibold text-ink transition-colors hover:brightness-95 disabled:opacity-60"
          >
            {login.isPending ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
